import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { DOCUMENT, documentPageContainerSx } from '../constants/documentTheme';
import { PAGE_CONTAINER_MAX_WIDTH } from '../constants/documentTheme';

const fieldSx = {
  mb: 2,
  '& .MuiOutlinedInput-root': { borderRadius: 0 },
};

const PartnerIntakePublic = () => {
  const { token } = useParams();
  const { t } = useTranslation();
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    destination: '',
    startDate: '',
    endDate: '',
    travelers: '2',
    budget: '',
    interests: '',
    hotelPreference: '',
    flightPreference: '',
    notes: '',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/public/partner-intake/${encodeURIComponent(token)}`);
        if (cancelled) return;
        setLink(res.data.link);
        setForm((p) => ({
          ...p,
          destination: res.data.link?.defaultDestination || '',
          budget: res.data.link?.defaultBudgetHint || '',
        }));
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || t('partnerIntake.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const onChange = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await api.post(`/public/partner-intake/${encodeURIComponent(token)}`, form);
      setDone(true);
    } catch (err) {
      setError(err.response?.data?.error || t('partnerIntake.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const accent = link?.branding?.color || DOCUMENT.oxblood;

  if (loading) {
    return (
      <Box sx={{ py: 10, textAlign: 'center' }}>
        <CircularProgress sx={{ color: DOCUMENT.oxblood }} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: DOCUMENT.paper, py: { xs: 4, md: 6 } }}>
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={documentPageContainerSx}>
        <Typography
          sx={{
            fontFamily: DOCUMENT.display,
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            color: DOCUMENT.ink,
            mb: 1,
          }}
        >
          {link?.label || t('partnerIntake.title')}
        </Typography>
        <Typography sx={{ color: DOCUMENT.ink3, mb: 3 }}>
          {t('partnerIntake.subtitle', { partner: link?.partnerName || 'your advisor' })}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 0 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {done ? (
          <Alert severity="success" sx={{ borderRadius: 0 }}>
            {t('partnerIntake.success')}
          </Alert>
        ) : (
          <Box component="form" onSubmit={submit} sx={{ maxWidth: 560 }}>
            <TextField required fullWidth label={t('partnerIntake.name')} value={form.name} onChange={onChange('name')} sx={fieldSx} />
            <TextField required fullWidth type="email" label={t('partnerIntake.email')} value={form.email} onChange={onChange('email')} sx={fieldSx} />
            <TextField fullWidth label={t('partnerIntake.phone')} value={form.phone} onChange={onChange('phone')} sx={fieldSx} />
            <TextField fullWidth label={t('partnerIntake.destination')} value={form.destination} onChange={onChange('destination')} sx={fieldSx} />
            <TextField required fullWidth type="date" InputLabelProps={{ shrink: true }} label={t('partnerIntake.startDate')} value={form.startDate} onChange={onChange('startDate')} sx={fieldSx} />
            <TextField required fullWidth type="date" InputLabelProps={{ shrink: true }} label={t('partnerIntake.endDate')} value={form.endDate} onChange={onChange('endDate')} sx={fieldSx} />
            <TextField select fullWidth label={t('partnerIntake.travelers')} value={form.travelers} onChange={onChange('travelers')} sx={fieldSx}>
              {[1, 2, 3, 4, 5, 6, 8, 10].map((n) => (
                <MenuItem key={n} value={String(n)}>{n}</MenuItem>
              ))}
            </TextField>
            <TextField fullWidth label={t('partnerIntake.budget')} value={form.budget} onChange={onChange('budget')} sx={fieldSx} />
            <TextField fullWidth label={t('partnerIntake.interests')} value={form.interests} onChange={onChange('interests')} sx={fieldSx} />
            <TextField fullWidth label={t('partnerIntake.hotelPreference')} value={form.hotelPreference} onChange={onChange('hotelPreference')} sx={fieldSx} />
            <TextField fullWidth label={t('partnerIntake.flightPreference')} value={form.flightPreference} onChange={onChange('flightPreference')} sx={fieldSx} />
            <TextField fullWidth multiline minRows={3} label={t('partnerIntake.notes')} value={form.notes} onChange={onChange('notes')} sx={fieldSx} />
            <Button
              type="submit"
              disabled={submitting}
              sx={{
                ...{ bgcolor: accent, color: '#fff', borderRadius: 0, px: 3, py: 1.25, '&:hover': { bgcolor: accent } },
              }}
            >
              {submitting ? t('partnerIntake.submitting') : t('partnerIntake.submit')}
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default PartnerIntakePublic;
